import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Divider,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Collapse,
  IconButton,
  Grid2 as Grid,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import { Theme } from '@mui/material/styles';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import MuiTableCell from '@mui/material/TableCell';
import UserEditorBase from '../UserEditorBase';
import { permissionNamesMap } from './permissionsData';
import DummyTableCell from './DummyTableCell';
import { UserPermissions, UserRole } from '../../../model/types';

const TableCell = withStyles({
  root: {
    borderBottom: 'none',
  },
})(MuiTableCell);

const useStyles = makeStyles((theme: Theme) => ({
  formTitleSubContainer: {
    padding: theme.spacing(3),
  },
  userFormSubContainer: {
    [`${theme.breakpoints.up('sm')}`]: {
      padding: theme.spacing(3),
    },
  },
  tableRow: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableHead: {
    backgroundColor: theme.palette.background.default,
  },
  collapsibleRow: {
    height: 0,
  },
  collapse: {
    backgroundColor: theme.palette.background.default,
  },
  childModuleNameCell: {
    paddingLeft: theme.spacing(2),
  },
  passwordContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  passwordField: {
    [`${theme.breakpoints.down('md')}`]: {
      marginBottom: theme.spacing(2),
    },
  },
  editorPaper: {
    [`${theme.breakpoints.down('sm')}`]: {
      marginLeft: -theme.spacing(3),
      marginRight: -theme.spacing(3),
    },
  },
  permissionsTitleContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  headerCell: {
    paddingLeft: theme.spacing(2),
  },
}));

const UserPermissionsEditor = ({
  editorMode,
}: {
  editorMode: 'create' | 'edit';
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const formik = useFormikContext<{
    password: string;
    createRole: UserRole;
    permissions: UserPermissions;
  }>();

  const { values, setFieldValue, errors, touched, getFieldProps } = formik;

  const generatePassword = () => {
    const length = 8;
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0, n = charset.length; i < length; ++i) {
      password += charset.charAt(Math.floor(Math.random() * n));
    }

    setFieldValue('password', password);
  };

  const extractCollapsibleStateValues = () => {
    const modulesWithChildren = permissionNamesMap.filter(
      module => !!module.children
    );

    const collapsibleStatesMap: Record<string, boolean> = {};

    if (modulesWithChildren.length > 0) {
      modulesWithChildren.forEach(module => {
        collapsibleStatesMap[module.value] = false;
      });
    }

    return collapsibleStatesMap;
  };

  const [collapsibles, setCollapsibles] = useState(
    extractCollapsibleStateValues()
  );

  const handleCollapsibleToggle = (moduleValue: string) => {
    const newCollapsibles = { ...collapsibles };
    newCollapsibles[moduleValue] = !newCollapsibles[moduleValue];

    return setCollapsibles(newCollapsibles);
  };

  const handleRegularModulePermissionChange = (
    moduleValue: string,
    permissionKey: string
  ) => {
    const currentPermission = values.permissions[moduleValue][permissionKey];

    return setFieldValue(
      `permissions.${moduleValue}.${permissionKey}`,
      !currentPermission
    );
  };

  const handleParentModulePermissionChange = (
    parentModuleValue: string,
    permissionKey: string
  ) => {
    const currentGlobalPermission =
      values.permissions[parentModuleValue].global[permissionKey];

    setFieldValue(
      `permissions.${parentModuleValue}.global.${permissionKey}`,
      !currentGlobalPermission
    );

    Object.keys(values.permissions[parentModuleValue].children).forEach(
      childModule => {
        setFieldValue(
          `permissions.${parentModuleValue}.children.${childModule}.${permissionKey}`,
          !currentGlobalPermission
        );
      }
    );
  };

  const handleChildModulePermissionChange = (
    parentModuleValue: string,
    childModuleValue: string,
    permissionKey: string
  ) => {
    const currentChildModulePermission =
      values.permissions[parentModuleValue].children[childModuleValue][
        permissionKey
      ];

    return setFieldValue(
      `permissions.${parentModuleValue}.children.${childModuleValue}.${permissionKey}`,
      !currentChildModulePermission
    );
  };

  return (
    <UserEditorBase>
      <Paper className={classes.editorPaper}>
        <div className={classes.formTitleSubContainer}>
          <Typography variant="h6">Security</Typography>
        </div>
        <Divider orientation="horizontal" />
        <div className={classes.userFormSubContainer}>
          <Grid
            spacing={1}
            container
            alignItems="flex-end"
            className={classes.passwordContainer}
          >
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                variant="standard"
                id="user-first-name-input"
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                helperText={errors.password}
                error={Boolean(touched.password && errors.password)}
                {...getFieldProps('password')}
                fullWidth
                className={classes.passwordField}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                fullWidth
                onClick={handleShowPassword}
                variant="contained"
                color="primary"
              >
                Show
              </Button>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                onClick={generatePassword}
                fullWidth
                variant="contained"
                color="primary"
              >
                Generate
              </Button>
            </Grid>
            {editorMode === 'create' && (
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  select
                  variant="standard"
                  id="user-role-input"
                  label={t('Role')}
                  name="createRole"
                  helperText={errors.createRole}
                  {...getFieldProps('createRole')}
                  fullWidth
                >
                  <MenuItem value="staff">{t('Staff')}</MenuItem>
                  <MenuItem value="admin">{t('Admin')}</MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
          <Grid className={classes.permissionsTitleContainer} container>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">Permissions</Typography>
            </Grid>
          </Grid>
          <TableContainer component={Paper}>
            <Table
              size="small"
              aria-label="user-permissions-table"
              stickyHeader
            >
              <TableHead className={classes.tableHead}>
                <TableRow className={classes.tableHead}>
                  <TableCell>Module</TableCell>
                  <TableCell className={classes.headerCell}>View</TableCell>
                  <TableCell className={classes.headerCell}>Create</TableCell>
                  <TableCell className={classes.headerCell}>Edit</TableCell>
                  <TableCell colSpan={2}>Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissionNamesMap.length > 0 &&
                  permissionNamesMap.map(module => {
                    if (module.children)
                      return (
                        <React.Fragment key={module.value}>
                          <TableRow className={classes.tableRow}>
                            <TableCell width="32%">
                              <Typography variant="inherit">
                                {module.name}
                              </Typography>
                            </TableCell>
                            {Object.keys(
                              values.permissions[module.value].global
                            ).map(permissionKey => (
                              <TableCell
                                width="14%"
                                key={`${module.value}-${permissionKey}`}
                              >
                                <Checkbox
                                  color="primary"
                                  checked={
                                    values.permissions[module.value].global[
                                      permissionKey
                                    ]
                                  }
                                  onChange={() =>
                                    handleParentModulePermissionChange(
                                      module.value,
                                      permissionKey
                                    )
                                  }
                                />
                              </TableCell>
                            ))}
                            <TableCell>
                              <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={() =>
                                  handleCollapsibleToggle(module.value)
                                }
                              >
                                {collapsibles[module.value] ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          <TableRow className={classes.collapsibleRow}>
                            <TableCell style={{ padding: 0 }} colSpan={6}>
                              <Collapse
                                className={classes.collapse}
                                in={collapsibles[module.value]}
                              >
                                <Table size="small">
                                  <TableBody>
                                    {module.children.map(childModule => (
                                      <TableRow key={childModule.value}>
                                        <TableCell width="32%">
                                          <Typography variant="body2">
                                            {childModule.name}
                                          </Typography>
                                        </TableCell>
                                        {Object.keys(
                                          values.permissions[module.value]
                                            .children[childModule.value]
                                        ).map(permissionKey => (
                                          <TableCell
                                            width="14%"
                                            key={`${module.value}-${childModule.value}-${permissionKey}`}
                                          >
                                            <Checkbox
                                              color="primary"
                                              checked={
                                                values.permissions[module.value]
                                                  .children[childModule.value][
                                                  permissionKey
                                                ]
                                              }
                                              onChange={() =>
                                                handleChildModulePermissionChange(
                                                  module.value,
                                                  childModule.value,
                                                  permissionKey
                                                )
                                              }
                                            />
                                          </TableCell>
                                        ))}
                                        <DummyTableCell />
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );

                    return (
                      <TableRow className={classes.tableRow} key={module.value}>
                        <TableCell width="32%">
                          <Typography variant="inherit">
                            {module.name}
                          </Typography>
                        </TableCell>
                        {values.permissions[module.value] &&
                          Object.keys(values.permissions[module.value]).map(
                            permissionKey => (
                              <TableCell
                                width="14%"
                                key={`${module.value}-${permissionKey}`}
                              >
                                <Checkbox
                                  color="primary"
                                  checked={
                                    values.permissions[module.value][
                                      permissionKey
                                    ]
                                  }
                                  onChange={() =>
                                    handleRegularModulePermissionChange(
                                      module.value,
                                      permissionKey
                                    )
                                  }
                                />
                              </TableCell>
                            )
                          )}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Paper>
    </UserEditorBase>
  );
};

export default UserPermissionsEditor;
